import { Dialog, DialogType, getTheme, PrimaryButton, Stack, Text } from '@fluentui/react';
import React from 'react';
import IvicosStrings from 'kits/language/stringKit';

export interface IFocusExtendEndModalProps {
    isOpen: boolean;
    onExtend: () => void;
    onEnd: () => void;
    onDismiss: () => void;
}

/**
 * Modal shown when focus timer expires without user action.
 * User can extend (open FocusTimeModal again) or end (switch to available).
 */
const FocusExtendEndModal: React.FC<IFocusExtendEndModalProps> = ({ isOpen, onExtend, onEnd, onDismiss }) => {
    const theme = getTheme();
    const [elapsedSeconds, setElapsedSeconds] = React.useState(0);
    const modalOpenTimeRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            if (!modalOpenTimeRef.current) {
                modalOpenTimeRef.current = Date.now();
            }
            const interval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - (modalOpenTimeRef.current || Date.now())) / 1000);
                setElapsedSeconds(elapsed);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            modalOpenTimeRef.current = null;
            setElapsedSeconds(0);
        }
    }, [isOpen]);

    const formatElapsedTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const dialogTitle = `${IvicosStrings.focusExtendEndModalTitle} ${formatElapsedTime(elapsedSeconds)}`;

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.close,
                title: dialogTitle,
                closeButtonAriaLabel: IvicosStrings.focusExtendEndCloseAriaLabel
            }}
            modalProps={{ isBlocking: true }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack tokens={{ childrenGap: 20 }}>
                <Text variant="medium">{IvicosStrings.focusExtendEndModalMessage}</Text>
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                    <PrimaryButton text={IvicosStrings.focusExtendEndExtend} onClick={onExtend} />
                    <button
                        type="button"
                        onClick={onEnd}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 4,
                            border: '1px solid ' + theme.palette.neutralLight,
                            backgroundColor: 'transparent',
                            color: theme.palette.black,
                            cursor: 'pointer',
                            fontSize: 14
                        }}
                    >
                        {IvicosStrings.focusExtendEndEnd}
                    </button>
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default FocusExtendEndModal;
